import { useEffect, useState, type ReactNode } from 'react';
import {
  BRIEF_SECTION_LABELS,
  BRIEF_SECTION_ORDER,
  briefSectionFor,
  facetSummaryKind,
  factLine,
  type Analysis,
  type BriefSection,
  type FacetCoverage,
  type Fact,
  type HeldClaim,
} from '@ni/shared';
import { useOrg } from '../../api/app-context';
import { FactRow } from './fact-row';
import {
  EVIDENCE,
  evidenceDisclosureLabel,
  FACT_TYPE_LABELS,
  filterFactsPlaceholder,
  moreFromSourceLine,
  moreSourcesLine,
  NO_MATCHES,
  SEARCHED_STATUS_WORDS,
  sourceTierLabel,
} from '../../lib/copy';
import { analysisCaveats, analysisOfKind, analysisProse } from '../../lib/analysis-copy';
import { Caveats } from '../ui/caveats';
import { Prose } from '../ui/prose';
import { NetWorthBlock, hasNetWorthBand } from './net-worth';
import { formatDate, formatDay, plural } from '../../lib/format';
import {
  sourceCount,
  sourceHost,
  sourceTitle,
  tierTitle,
  type FactSource,
  type SourceIndex,
} from './fact-sources';
import './evidence.css';
import '../record/record-body.css';

/** Findings shown per source before "N more from this source". */
const PER_SOURCE = 3;
/** Sources shown before "Show N more sources". */
const SOURCES_SHOWN = 4;

/**
 * A research panel's head: the section's name in the reading face, and on the
 * right whether this is the finished brief or the live run's draft.
 */
export function PanelHead({ title, meta }: { title: ReactNode; meta?: ReactNode }) {
  return (
    <div className="p-head">
      <h2>{title}</h2>
      {meta ? <p className="p-meta">{meta}</p> : null}
    </div>
  );
}

/** "As of Sep 18, 2026", or "Draft · from this run" while a run is writing it. */
export function panelMeta(analysis: Analysis | undefined | null, draft: boolean): string | null {
  if (draft) return 'Draft · from this run';
  if (!analysis) return null;
  return `As of ${formatDay(analysis.created_at)}`;
}

interface CoverageRow {
  source: string;
  status: FacetCoverage['status'];
}

/**
 * The panel's "Searched" line, from the facet summary's own coverage where it
 * carries one. Read defensively: `content` is passthrough, and a row that does
 * not have both a name and a known status says nothing rather than something
 * half-true.
 */
function coverageOf(analysis: Analysis | null | undefined): CoverageRow[] {
  const raw = (analysis?.content as Record<string, unknown> | undefined)?.coverage;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((row) => {
    if (!row || typeof row !== 'object') return [];
    const { source, status } = row as Record<string, unknown>;
    if (typeof source !== 'string' || typeof status !== 'string') return [];
    if (!(status in SEARCHED_STATUS_WORDS)) return [];
    return [{ source, status: status as FacetCoverage['status'] }];
  });
}

/**
 * The research panels (blend spec, step 4): depth on demand. Each section leads
 * with its summary in the reading face, then one closed disclosure, "Show the N
 * facts behind this · M sources", which holds what was searched, a filter, the
 * typed facts in a label column with source and date in the margin, the records
 * checks in calm words, and the findings grouped by the source they came from.
 *
 * Sections are driven by **fact type**, never by the facet that produced the
 * fact (facts carry no facet) — the mapping is `FACT_TYPE_BRIEF_SECTION` in
 * `@ni/shared`. A facet summary, where one exists, is the section's summary,
 * set apart from what is on record so interpretation never passes for it.
 */
export function FactSections({
  subjectId,
  subjectName,
  facts,
  heldClaims,
  analyses,
  queuedByLineage,
  runId,
  initialOpen = null,
  sections: only = BRIEF_SECTION_ORDER,
  draft = false,
  sources: index = null,
}: {
  subjectId: string;
  subjectName: string;
  facts: Fact[];
  heldClaims: HeldClaim[];
  analyses: Analysis[];
  queuedByLineage: Map<string, string>;
  runId: string | null;
  /** The section a deep link arrives asking for: its facts are open on arrival. */
  initialOpen?: BriefSection | null;
  /** Which sections this instance renders, in `BRIEF_SECTION_ORDER`. */
  sections?: readonly BriefSection[];
  /** The panel is the live run's draft rather than the finished brief. */
  draft?: boolean;
  /** Each fact's sources, from the Data tab's read (`useSourceIndex`). */
  sources?: SourceIndex | null;
}) {
  const sections = BRIEF_SECTION_ORDER.filter((section) => only.includes(section))
    .map((section) => {
      const summary = analysisOfKind(analyses, facetSummaryKind(section));
      return {
        section,
        facts: facts.filter((fact) => briefSectionFor(fact.fact_type) === section),
        held: heldClaims.filter((claim) => briefSectionFor(claim.fact_type) === section),
        summary,
        paragraph: analysisProse(summary),
        // The facet summary's own hedging, the same field the ledes render.
        caveats: analysisCaveats(summary),
      };
    })
    /* Finances also stands on the estimated net worth alone: a member with a
       band and no money facts still has a Finances section to read it in. */
    .filter(
      (entry) =>
        entry.facts.length > 0 ||
        entry.held.length > 0 ||
        (entry.section === 'finances' && hasNetWorthBand(analyses)),
    );

  /* Nothing in *this instance's* sections is not the same statement as nothing
     on record: the empty record is the page's to say, and it says it. */
  if (sections.length === 0) return null;

  return (
    <>
      {sections.map((entry) => (
        <section
          className="r-panel"
          key={entry.section}
          aria-label={BRIEF_SECTION_LABELS[entry.section]}
        >
          <PanelHead
            title={BRIEF_SECTION_LABELS[entry.section]}
            meta={panelMeta(entry.summary, draft)}
          />
          {entry.section === 'finances' ? <NetWorthBlock analyses={analyses} /> : null}
          {entry.paragraph ? (
            <div className="reading">
              <Prose text={entry.paragraph} />
            </div>
          ) : null}
          <Caveats caveats={entry.caveats} />
          {entry.facts.length > 0 || entry.held.length > 0 ? (
            <Evidence
              subjectId={subjectId}
              subjectName={subjectName}
              facts={entry.facts}
              held={entry.held}
              coverage={coverageOf(entry.summary)}
              queuedByLineage={queuedByLineage}
              runId={runId}
              index={index}
              openOnArrival={initialOpen === entry.section}
            />
          ) : null}
        </section>
      ))}
    </>
  );
}

function SourceNote({ vertical, source }: { vertical: string; source: FactSource | null }) {
  if (!source) return <p className="src-note" />;
  const host = sourceHost(source.url);
  return (
    <p className={source.office ? 'src-note office' : 'src-note'}>
      <span className="t">{sourceTitle(vertical, source)}</span>
      {source.date ? <span className="dt">{formatDate(source.date)}</span> : null}
      {host && !source.date ? <span className="dt host">{host}</span> : null}
    </p>
  );
}

function Sub({ title, count, margin }: { title: string; count: ReactNode; margin: boolean }) {
  return (
    <div className="sub">
      <h3>{title}</h3>
      <span className="c">{count}</span>
      {margin ? <span className="r">{EVIDENCE.source}</span> : null}
    </div>
  );
}

const NO_QUEUED = new Map<string, string>();

/**
 * The one closed disclosure, "Show the N facts behind this · M sources". The
 * research panels feed it a section's facts; a record with one reading (an
 * organization) feeds it every fact it holds, read-only, with `line` writing
 * each sentence for that record's reader.
 */
export function Evidence({
  subjectId,
  subjectName,
  facts,
  held = [],
  coverage = [],
  queuedByLineage = NO_QUEUED,
  runId = null,
  index,
  openOnArrival = false,
  line,
  readOnly = false,
}: {
  subjectId: string;
  subjectName: string;
  facts: Fact[];
  held?: HeldClaim[];
  coverage?: CoverageRow[];
  queuedByLineage?: Map<string, string>;
  runId?: string | null;
  index: SourceIndex | null;
  openOnArrival?: boolean;
  line?: (fact: Fact) => string;
  readOnly?: boolean;
}) {
  const { vertical } = useOrg();
  /* A row the office still owes an answer on is the ask itself: behind a closed
     disclosure it can neither be seen nor reached by anchor. So a panel holding
     one opens, everything in it shows, and it stays that way for the rest of
     the visit — answering the last one must not shut the panel over its own
     "Kept · just now". A fresh visit starts unlatched. */
  const hasQueued = facts.some(
    (fact) => fact.status === 'active' && queuedByLineage.has(fact.lineage_id),
  );
  const [latched, setLatched] = useState(hasQueued);
  useEffect(() => {
    if (hasQueued) setLatched(true);
  }, [hasQueued]);
  const [open, setOpen] = useState(openOnArrival);
  const [query, setQuery] = useState('');
  const [expandAll, setExpandAll] = useState(openOnArrival);
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
  const [allSources, setAllSources] = useState(false);

  const isOpen = open || latched;
  const unfolded = expandAll || latched || query.trim().length > 0;

  const reader = { id: subjectId, name: subjectName };
  const q = query.trim().toLowerCase();
  const primary = (fact: Fact) => index?.get(fact.lineage_id)?.[0] ?? null;
  const matches = (text: string) => !q || text.toLowerCase().includes(q);
  const factText = (fact: Fact) => {
    const source = primary(fact);
    return `${FACT_TYPE_LABELS[fact.fact_type]} ${line ? line(fact) : factLine(fact, reader)} ${
      source ? sourceTierLabel(vertical, source.tier) : ''
    }`;
  };

  const typed = facts.filter((f) => f.fact_type !== 'finding' && f.fact_type !== 'records_check');
  const checks = facts.filter((f) => f.fact_type === 'records_check');
  const findings = facts.filter((f) => f.fact_type === 'finding');
  const total = facts.length + held.length;
  const sources = index
    ? sourceCount(
        index,
        facts.map((f) => f.lineage_id),
      )
    : null;

  /* Findings grouped by the document they were read from, largest first. Before
     the sources land, they are one group with no margin. */
  const groups = (() => {
    const byKey = new Map<string, { key: string; source: FactSource | null; facts: Fact[] }>();
    for (const fact of findings) {
      const source = index?.get(fact.lineage_id)?.[0] ?? null;
      const key = source?.key ?? '';
      const group = byKey.get(key) ?? { key, source, facts: [] };
      group.facts.push(fact);
      byKey.set(key, group);
    }
    return [...byKey.values()].sort((a, b) => b.facts.length - a.facts.length);
  })();

  const shownTyped = typed.filter((f) => matches(factText(f)));
  const shownHeld = held.filter((c) =>
    matches(`${FACT_TYPE_LABELS[c.fact_type]} ${c.evidence_excerpt ?? ''}`),
  );
  const shownChecks = checks.filter((f) => matches(factText(f)));
  const shownGroups = groups
    .map((group) => ({ ...group, facts: group.facts.filter((f) => matches(factText(f))) }))
    .filter((group) => group.facts.length > 0);
  const visibleGroups = unfolded || allSources ? shownGroups : shownGroups.slice(0, SOURCES_SHOWN);
  const hiddenGroups = shownGroups.slice(visibleGroups.length);
  const hits =
    shownTyped.length +
    shownHeld.length +
    shownChecks.length +
    shownGroups.reduce((sum, g) => sum + g.facts.length, 0);
  const foldable =
    groups.length > SOURCES_SHOWN || groups.some((group) => group.facts.length > PER_SOURCE);

  const row = (fact: Fact, variant: 'row' | 'finding') => (
    <FactRow
      key={fact.lineage_id}
      fact={fact}
      subjectId={subjectId}
      subjectName={subjectName}
      queuedDecisionId={queuedByLineage.get(fact.lineage_id) ?? null}
      runId={runId}
      variant={variant}
      source={primary(fact)}
      line={line ? line(fact) : null}
      readOnly={readOnly}
    />
  );

  return (
    <div className="evidence">
      {latched ? (
        <p className="disclose-plus static">
          {evidenceDisclosureLabel(total)}
          {sources ? (
            <span className="quiet"> · {plural(sources, 'source', 'sources')}</span>
          ) : null}
        </p>
      ) : (
        <button
          type="button"
          className="disclose-plus"
          aria-expanded={isOpen}
          onClick={() => setOpen((value) => !value)}
        >
          {evidenceDisclosureLabel(total)}
          {sources ? (
            <span className="quiet"> · {plural(sources, 'source', 'sources')}</span>
          ) : null}
        </button>
      )}

      {isOpen ? (
        <div className="evidence-body">
          {coverage.length > 0 ? (
            <p className="looked">
              <span className="sc">{EVIDENCE.searched}</span>
              {coverage.map((row, i) => (
                <span key={`${i}-${row.source}`}>
                  {i > 0 ? ' · ' : null}
                  <b>{row.source}</b>, {SEARCHED_STATUS_WORDS[row.status]}
                </span>
              ))}
            </p>
          ) : null}

          {total > 1 ? (
            <div className="p-tools">
              <label className="filter">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <circle cx="7" cy="7" r="4.5" />
                  <path d="m10.5 10.5 3 3" />
                </svg>
                <input
                  type="search"
                  value={query}
                  placeholder={filterFactsPlaceholder(total)}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
              {q ? (
                <span className="match">
                  {hits} of {total}
                </span>
              ) : null}
              {foldable && !latched && !q ? (
                <button
                  type="button"
                  className="textbtn"
                  onClick={() => setExpandAll((value) => !value)}
                >
                  {expandAll ? EVIDENCE.collapse : EVIDENCE.expandAll}
                </button>
              ) : null}
            </div>
          ) : null}

          {shownTyped.length + shownHeld.length > 0 ? (
            <>
              <Sub title={EVIDENCE.onRecord} count={shownTyped.length + shownHeld.length} margin />
              <ul className="rows">
                {shownTyped.map((fact) => row(fact, 'row'))}
                {shownHeld.map((claim) => (
                  <li className="fact fact-row off" key={claim.claim_id}>
                    <span className="k">{FACT_TYPE_LABELS[claim.fact_type]}</span>
                    <div className="v">
                      {claim.evidence_excerpt ?? 'Unplaced finding'}
                      <span className="tag-faint">{EVIDENCE.pendingReview}</span>
                    </div>
                    <p className="src-note">
                      <span className="t">{tierTitle(vertical, claim.source_tier)}</span>
                      {claim.content_date ? (
                        <span className="dt">{formatDate(claim.content_date)}</span>
                      ) : null}
                    </p>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {shownChecks.length > 0 ? (
            <>
              <Sub title={EVIDENCE.checks} count={shownChecks.length} margin />
              <ul className="rows checks">{shownChecks.map((fact) => row(fact, 'row'))}</ul>
            </>
          ) : null}

          {shownGroups.length > 0 ? (
            <>
              <Sub
                title={EVIDENCE.findings}
                count={
                  index
                    ? `${shownGroups.reduce((sum, g) => sum + g.facts.length, 0)} from ${plural(
                        shownGroups.length,
                        'source',
                        'sources',
                      )}`
                    : shownGroups.reduce((sum, g) => sum + g.facts.length, 0)
                }
                margin={index !== null}
              />
              {visibleGroups.map((group) => {
                const whole = unfolded || openGroups.has(group.key);
                const items = whole ? group.facts : group.facts.slice(0, PER_SOURCE);
                const rest = group.facts.length - items.length;
                return (
                  <div className={index ? 'sg' : 'sg bare'} key={group.key}>
                    <ul className="sg-facts">
                      {items.map((fact) => row(fact, 'finding'))}
                      {rest > 0 ? (
                        <li className="sg-more">
                          <button
                            type="button"
                            className="textbtn"
                            onClick={() =>
                              setOpenGroups((current) => new Set(current).add(group.key))
                            }
                          >
                            {moreFromSourceLine(rest)}
                          </button>
                        </li>
                      ) : null}
                    </ul>
                    {index ? <SourceNote vertical={vertical} source={group.source} /> : null}
                  </div>
                );
              })}
              {hiddenGroups.length > 0 ? (
                <div className="more-row">
                  <button type="button" className="btn act" onClick={() => setAllSources(true)}>
                    {moreSourcesLine(hiddenGroups.length)}
                  </button>
                  <span className="quiet">
                    {plural(
                      hiddenGroups.reduce((sum, g) => sum + g.facts.length, 0),
                      'finding',
                      'findings',
                    )}
                  </span>
                </div>
              ) : null}
            </>
          ) : null}

          {q && hits === 0 ? <p className="no-results">{NO_MATCHES}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
